import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Factory, ShoppingCart, Play, CheckCircle, XCircle, AlertTriangle, Package, Clock, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
  completed: 'bg-green-100 text-green-800 border-green-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300'
};

const statusIcons = {
  pending: Clock,
  in_progress: RefreshCw,
  completed: CheckCircle,
  cancelled: XCircle
};

export default function FulfillmentTasksPage() {
  const { user, getAuthHeader } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreateWODialog, setShowCreateWODialog] = useState(false);
  const [showCreateIndentDialog, setShowCreateIndentDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  // Form states for WO/Indent creation
  const [indentForm, setIndentForm] = useState({
    quantity_required: 0,
    reason: ''
  });

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = activeTab !== 'all' ? { status: activeTab } : {};
      const response = await axios.get(`${API_URL}/fulfillment-tasks`, { 
        params,
        headers: getAuthHeader() 
      });
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch fulfillment tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [activeTab]);

  // Start working on a task
  const handleStartTask = async (task) => {
    try {
      await axios.post(`${API_URL}/fulfillment-tasks/${task.id}/start`, {}, { 
        headers: getAuthHeader() 
      });
      toast.success('Task started');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start task');
    }
  };

  // Complete a task
  const handleCompleteTask = async (task) => {
    try {
      await axios.post(`${API_URL}/fulfillment-tasks/${task.id}/complete`, {}, { 
        headers: getAuthHeader() 
      });
      toast.success('Task completed');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete task');
    }
  };

  // Cancel a task
  const handleCancelTask = async () => {
    if (!selectedTask) return;
    try {
      await axios.post(`${API_URL}/fulfillment-tasks/${selectedTask.id}/cancel`, {}, { 
        headers: getAuthHeader() 
      });
      toast.success('Task cancelled');
      setShowCancelDialog(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel task');
    }
  };

  // Open Create WO dialog
  const openCreateWO = (task) => {
    setSelectedTask(task);
    setShowCreateWODialog(true);
  };

  // Open Create Indent dialog
  const openCreateIndent = (task) => {
    setSelectedTask(task);
    setIndentForm({
      quantity_required: task.quantity,
      reason: `Fulfillment task ${task.task_number} - ${task.item_name} for SO ${task.so_number}`
    });
    setShowCreateIndentDialog(true);
  };

  // Create Work Order from task
  const handleCreateWO = async () => {
    if (!selectedTask) return;
    try {
      // Create work order using the existing API
      const response = await axios.post(`${API_URL}/work-orders`, {
        sales_order_id: selectedTask.sales_order_id,
        line_item_id: selectedTask.so_line_item_id
      }, { headers: getAuthHeader() });
      
      // Update the fulfillment task with WO reference
      await axios.patch(`${API_URL}/fulfillment-tasks/${selectedTask.id}`, {
        work_order_id: response.data.id,
        work_order_number: response.data.work_order_number,
        status: 'in_progress'
      }, { headers: getAuthHeader() });
      
      toast.success(`Work Order ${response.data.work_order_number} created`);
      setShowCreateWODialog(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create work order');
    }
  };

  // Create Indent from task
  const handleCreateIndent = async () => {
    if (!selectedTask) return;
    try {
      // Create indent using the existing API
      const response = await axios.post(`${API_URL}/indents`, {
        item_id: selectedTask.item_id,
        quantity_required: indentForm.quantity_required,
        reason: indentForm.reason
      }, { headers: getAuthHeader() });
      
      // Update the fulfillment task with Indent reference
      await axios.patch(`${API_URL}/fulfillment-tasks/${selectedTask.id}`, {
        indent_id: response.data.id,
        indent_number: response.data.indent_number,
        status: 'in_progress'
      }, { headers: getAuthHeader() });
      
      toast.success(`Indent ${response.data.indent_number} created`);
      setShowCreateIndentDialog(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create indent');
    }
  };

  // Get task stats
  const taskStats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    production: tasks.filter(t => t.task_type === 'production').length,
    purchase: tasks.filter(t => t.task_type === 'purchase').length
  };

  // Check user access for team assignment - owners/admins see all
  const userTeam = (user?.is_owner || user?.allowed_modules?.includes('fulfillment_tasks')) ? 'all' : 
                   user?.designation_name?.toLowerCase().includes('production') ? 'production' : 'purchase';

  // Filter tasks based on team (admin sees all)
  const filteredTasks = userTeam === 'all' ? tasks : 
    tasks.filter(t => t.assigned_to_team === userTeam || t.assigned_to_team === userTeam);

  if (!user) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Please login to access this page</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <p className="text-muted-foreground">
              Tasks generated when Sales Order fulfillment type contradicts item&apos;s default source
            </p>
          </div>
          <Button variant="outline" onClick={fetchTasks}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Tasks</p>
                  <p className="text-2xl font-bold">{taskStats.total}</p>
                </div>
                <Package className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{taskStats.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600">{taskStats.inProgress}</p>
                </div>
                <RefreshCw className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{taskStats.completed}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-700">For Production</p>
                  <p className="text-2xl font-bold text-orange-600">{taskStats.production}</p>
                </div>
                <Factory className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-700">For Purchase</p>
                  <p className="text-2xl font-bold text-purple-600">{taskStats.purchase}</p>
                </div>
                <ShoppingCart className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tasks Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle>Fulfillment Tasks</CardTitle>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="pending">Pending</TabsTrigger>
                  <TabsTrigger value="in_progress">In Progress</TabsTrigger>
                  <TabsTrigger value="completed">Completed</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Task #</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-center">Qty</TableHead>
                  <TableHead>SO #</TableHead>
                  <TableHead>Contradiction</TableHead>
                  <TableHead>Linked Doc</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : filteredTasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No fulfillment tasks found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTasks.map((task) => {
                    const StatusIcon = statusIcons[task.status] || Clock;
                    const isProduction = task.task_type === 'production';
                    
                    return (
                      <TableRow key={task.id} className="hover:bg-muted/30">
                        <TableCell className="font-mono text-sm font-medium">
                          {task.task_number}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={isProduction ? 'bg-orange-50 text-orange-700 border-orange-300' : 'bg-purple-50 text-purple-700 border-purple-300'}
                          >
                            {isProduction ? (
                              <><Factory className="w-3 h-3 mr-1" /> Production</>
                            ) : (
                              <><ShoppingCart className="w-3 h-3 mr-1" /> Purchase</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{task.item_name}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-medium">{task.quantity}</span>
                          <span className="text-xs text-muted-foreground ml-1">{task.unit}</span>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm">{task.so_number}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              Default: {task.item_source_type === 'IP' ? 'Make (IP)' : 'Buy (MP)'}
                            </Badge>
                            <span className="text-muted-foreground">→</span>
                            <Badge className={task.fulfillment_type === 'Make' ? 'bg-orange-100 text-orange-700' : 'bg-purple-100 text-purple-700'}>
                              Override: {task.fulfillment_type}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          {task.work_order_number && (
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              WO: {task.work_order_number}
                            </Badge>
                          )}
                          {task.indent_number && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              IND: {task.indent_number}
                            </Badge>
                          )}
                          {!task.work_order_number && !task.indent_number && (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[task.status]}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {task.status.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {task.status === 'pending' && (
                              <>
                                {isProduction ? (
                                  /* DEPRECATED: WO creation now happens through Production Fulfillment Register */
                                  /* Keep code for reference - button replaced with info badge */
                                  <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                                    Use Production Fulfillment
                                  </Badge>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    onClick={() => openCreateIndent(task)}
                                    className="bg-purple-600 hover:bg-purple-700"
                                  >
                                    <ShoppingCart className="w-3 h-3 mr-1" />
                                    Create Indent
                                  </Button>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedTask(task);
                                    setShowCancelDialog(true);
                                  }}
                                >
                                  <XCircle className="w-4 h-4 text-red-500" />
                                </Button>
                              </>
                            )}
                            {task.status === 'in_progress' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleCompleteTask(task)}
                                className="text-green-600 border-green-300 hover:bg-green-50"
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Complete
                              </Button>
                            )}
                            {task.status === 'completed' && (
                              <span className="text-xs text-muted-foreground">
                                {task.completed_at && format(new Date(task.completed_at), 'dd/MM/yy')}
                              </span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* DEPRECATED: Create Work Order Dialog - Kept for reference */}
        {/* WO creation now happens through Production Fulfillment Register (/production/fulfillment) */}
        {/* This dialog is no longer accessible as the button has been removed */}
        <AlertDialog open={showCreateWODialog} onOpenChange={setShowCreateWODialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Factory className="w-5 h-5 text-orange-600" />
                Create Work Order
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>This will create a Work Order for the following item:</p>
                  {selectedTask && (
                    <div className="bg-muted p-3 rounded-lg space-y-1">
                      <p><strong>Item:</strong> {selectedTask.item_name}</p>
                      <p><strong>Quantity:</strong> {selectedTask.quantity} {selectedTask.unit}</p>
                      <p><strong>Sales Order:</strong> {selectedTask.so_number}</p>
                      <div className="flex items-center gap-2 mt-2 p-2 bg-orange-50 rounded border border-orange-200">
                        <AlertTriangle className="w-4 h-4 text-orange-600" />
                        <span className="text-sm text-orange-700">
                          Item default is <strong>{selectedTask.item_source_type === 'IP' ? 'In-house Production' : 'Market Purchase'}</strong>, 
                          but SO requests <strong>{selectedTask.fulfillment_type}</strong>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleCreateWO} className="bg-orange-600 hover:bg-orange-700">
                <Factory className="w-4 h-4 mr-2" />
                Create Work Order
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Create Indent Dialog */}
        <Dialog open={showCreateIndentDialog} onOpenChange={setShowCreateIndentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-purple-600" />
                Create Indent (Purchase Requisition)
              </DialogTitle>
              <DialogDescription>
                Create an indent to purchase this item
              </DialogDescription>
            </DialogHeader>
            
            {selectedTask && (
              <div className="space-y-4 py-4">
                <div className="bg-muted p-3 rounded-lg space-y-1">
                  <p><strong>Item:</strong> {selectedTask.item_name}</p>
                  <p><strong>Sales Order:</strong> {selectedTask.so_number}</p>
                  <div className="flex items-center gap-2 mt-2 p-2 bg-purple-50 rounded border border-purple-200">
                    <AlertTriangle className="w-4 h-4 text-purple-600" />
                    <span className="text-sm text-purple-700">
                      Item default is <strong>{selectedTask.item_source_type === 'IP' ? 'In-house Production' : 'Market Purchase'}</strong>, 
                      but SO requests <strong>{selectedTask.fulfillment_type}</strong>
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="indent_qty">Quantity Required</Label>
                  <Input
                    id="indent_qty"
                    type="number"
                    value={indentForm.quantity_required}
                    onChange={(e) => setIndentForm({...indentForm, quantity_required: parseFloat(e.target.value) || 0})}
                    min={1}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="indent_reason">Reason</Label>
                  <Input
                    id="indent_reason"
                    value={indentForm.reason}
                    onChange={(e) => setIndentForm({...indentForm, reason: e.target.value})}
                  />
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateIndentDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateIndent} className="bg-purple-600 hover:bg-purple-700">
                <ShoppingCart className="w-4 h-4 mr-2" />
                Create Indent
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Task Dialog */}
        <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Fulfillment Task?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to cancel task <strong>{selectedTask?.task_number}</strong>? 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep Task</AlertDialogCancel>
              <AlertDialogAction onClick={handleCancelTask} className="bg-red-600 hover:bg-red-700">
                Cancel Task
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
